import {AfterViewChecked, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {Subscription} from 'rxjs/Subscription';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {UserService} from '../../services/user.service';
import {Router} from '@angular/router';
import {ViewDocxComponent} from '../views/view-docx/view-docx.component';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {storeDocumentContext} from '../../services/view-docx.service';
import {PhoneStandardsContext, StandardTestingLayout} from './phone-standards-context';
import {STANDARD_READ_QUERY, STANDARD_READ_RUSSIAN, STANDARD_READ_ENGLISH} from './standard-reader-document';
import {StandardFiles} from '../../models/standard-files.model';
import {StandardType} from '../../models/standard-type.model';
import {catalogScroll, downloadCatalogFile, openCatalogPdf, readCatalogState,
  restoreCatalogScroll, saveCatalogState} from './standard-catalog-state';

interface CatalogStandard extends StandardFiles { standardType?: StandardType; blank?: boolean; }
interface CatalogPage { content: CatalogStandard[]; totalElements: number; totalPages: number; }
const FIRST_PAGE = 1;
const HTTP_OK = 200;
const HTTP_EMPTY = 204;
const DEFAULT_SORT = 'number,asc';
const RUSSIAN = 'russian';
const PERSONAL_SOURCE = 'personal';
const STANDARD_ROUTE = '/dashboard/standard';
const EMPTY_TEXT = '';
const NO_ITEMS = 0;
const MODAL_OPTIONS = {size: 'lg' as 'lg', backdrop: 'static' as 'static'};
const FILENAME_HEADER = 'filename';

@Component({
  selector: 'app-phone-standards',
  templateUrl: './phone-standards.component.html',
  styleUrls: ['./phone-standards.component.css'],
  providers: [PhoneStandardsContext, StandardTestingLayout]
})
export class PhoneStandardsComponent implements OnInit, OnDestroy, AfterViewChecked {
  isLoaded = false;
  standards: CatalogStandard[] = [];
  internalServerProblemError = false;
  materialError = false;
  currentSortId = DEFAULT_SORT;
  hideBlanks = false;
  currentSearchValue = EMPTY_TEXT;
  searchDraft = EMPTY_TEXT;
  p = FIRST_PAGE;
  totalItems = NO_ITEMS;
  private userId = EMPTY_TEXT;
  private request: Subscription;
  private destroyed$ = new Subject<void>();
  private documentModal: NgbModalRef;
  private scrollToRestore = NO_ITEMS;

  get hasStandards(): boolean { return this.standards.length > NO_ITEMS; }
  get hasEmptyStandards(): boolean { return this.isLoaded && !this.hasStandards && !this.currentSearchValue && !this.internalServerProblemError; }
  get standardNotFound(): boolean { return this.isLoaded && !this.hasStandards && !!this.currentSearchValue && !this.internalServerProblemError; }
  get canShowStandards(): boolean { return this.hasStandards && !this.internalServerProblemError; }
  get isRussian(): boolean { return this.context.language.getLanguage() === RUSSIAN; }
  get userService(): UserService { return this.context.userService; }

  constructor(private context: PhoneStandardsContext, private router: Router, private modalService: NgbModal) {}

  ngOnInit(): void {
    const user = this.userService.getLocal();
    this.userId = user.id;
    const state = readCatalogState(this.userId);
    this.currentSearchValue = this.searchDraft = state.search;
    this.currentSortId = state.sort;
    this.p = state.page;
    this.scrollToRestore = state.scroll;
    this.hideBlanks = user.testing === true;
    if (this.hideBlanks) { this.context.testingLayout.apply(); }
    this.getAllStandards();
  }

  ngOnDestroy(): void {
    this.rememberState();
    if (this.request) { this.request.unsubscribe(); }
    if (this.documentModal) { this.documentModal.dismiss(); }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewChecked(): void {
    if (!this.isLoaded || !this.scrollToRestore || this.internalServerProblemError) { return; }
    restoreCatalogScroll(this.scrollToRestore);
    this.scrollToRestore = NO_ITEMS;
  }

  getAllStandards(): void {
    if (this.request) { this.request.unsubscribe(); }
    this.isLoaded = false;
    this.internalServerProblemError = false;
    this.request = this.userService.getPageServer(this.userId, this.currentSearchValue, this.p - FIRST_PAGE, this.currentSortId)
      .subscribe((response: HttpResponse<CatalogPage>) => this.acceptPage(response), () => this.rejectPage());
  }

  searchValue(value: string): void {
    this.currentSearchValue = value.trim();
    this.searchDraft = value;
    this.p = FIRST_PAGE;
    this.scrollToRestore = NO_ITEMS;
    this.rememberState();
    this.getAllStandards();
  }

  sortList(sort: string): void {
    this.currentSortId = sort;
    this.p = FIRST_PAGE;
    this.rememberState();
    this.getAllStandards();
  }

  getPage(page: number): void {
    this.p = page;
    this.rememberState();
    this.getAllStandards();
  }

  private acceptPage(response: HttpResponse<CatalogPage>): void {
    if (response.status !== HTTP_OK && response.status !== HTTP_EMPTY) { this.rejectPage(); return; }
    const page = response.body;
    this.standards = page && page.content || [];
    this.totalItems = page && page.totalElements || NO_ITEMS;
    if (!this.hasStandards && this.p > FIRST_PAGE) {
      this.p = FIRST_PAGE;
      this.scrollToRestore = NO_ITEMS;
      this.rememberState();
      this.getAllStandards();
      return;
    }
    this.isLoaded = true;
  }

  private rejectPage(): void {
    this.internalServerProblemError = true;
    this.isLoaded = true;
  }

  @HostListener('window:beforeunload') rememberState(): void {
    if (!this.userId) { return; }
    saveCatalogState(this.userId, {search: this.currentSearchValue, sort: this.currentSortId, page: this.p, scroll: catalogScroll()});
  }

  readStandard(standard: CatalogStandard): void {
    this.rememberState();
    if (!this.hideBlanks) {
      const queryParams = {source: PERSONAL_SOURCE};
      queryParams[STANDARD_READ_QUERY] = this.isRussian ? STANDARD_READ_RUSSIAN : STANDARD_READ_ENGLISH;
      this.router.navigate([STANDARD_ROUTE, standard.id], {queryParams});
      return;
    }
    const link = this.isRussian ? standard.ruFileLink : standard.enFileLink;
    const documentId = this.isRussian ? standard.ruDoc : standard.enDoc;
    if (link) { this.openDocModalDOCX(link); }
    else if (documentId) { this.openDocModal(documentId, standard.id); }
    else { this.materialError = true; }
  }

  openDocModal(documentId: string, standardId: string): void {
    storeDocumentContext({fileId: documentId, standardId: standardId, fileLink: null});
    this.openDocument();
  }

  openDocModalDOCX(link: string): void {
    storeDocumentContext({fileId: null, standardId: null, fileLink: link});
    this.openDocument();
  }

  private openDocument(): void {
    if (this.documentModal) { return; }
    this.materialError = false;
    this.documentModal = this.modalService.open(ViewDocxComponent, MODAL_OPTIONS);
    const cleanup = () => {
      this.documentModal = null;
      storeDocumentContext({fileId: null, standardId: null, fileLink: null});
    };
    this.documentModal.result.then(cleanup, cleanup);
  }

  downloadFile(fileId: string): void {
    this.materialError = false;
    this.userService.getFile(fileId).takeUntil(this.destroyed$).subscribe(
      (response: HttpResponse<Blob>) => downloadCatalogFile(response.body, response.headers.get(FILENAME_HEADER)),
      () => this.materialError = true);
  }

  openPdf(fileId: string): void {
    this.materialError = false;
    this.userService.getPdf(fileId).takeUntil(this.destroyed$).subscribe(
      (response: HttpResponse<Blob>) => openCatalogPdf(response.body), () => this.materialError = true);
  }

  trackStandard(index: number, standard: CatalogStandard): string { return standard.id; }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-standards/phone-standards.component.ts